import { Component, ChangeDetectionStrategy, input, output, inject, computed, signal } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { Clipboard, ClipboardModule } from '@angular/cdk/clipboard';
import { Field, StructureType } from '../../../types/fields';
import { FieldTypeIconComponent } from '../../../components/field-type-icon.component';

@Component({
  selector: 'app-field-card',
  imports: [
    MatIconModule,
    MatTooltipModule,
    MatSnackBarModule,
    ClipboardModule,
    FieldTypeIconComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div
      data-testid="field-card"
      class="group relative flex flex-col rounded-lg border bg-white dark:bg-zinc-800 shadow-sm transition-all"
      [class.p-3]="actionable()"
      [class.gap-2]="actionable()"
      [class.p-4]="!actionable()"
      [class.gap-3]="!actionable()"
      [class.border-zinc-200]="!highlightColor()"
      [class.dark:border-zinc-700]="!highlightColor()"
      [class.hover:border-zinc-300]="!highlightColor()"
      [class.dark:hover:border-zinc-600]="!highlightColor()"
      [class.hover:shadow-md]="true"
      [style.borderColor]="highlightColor()"
      [style.boxShadow]="highlightColor() ? '0 0 0 1px ' + highlightColor() : null"
    >
      @if (isDeleting()) {
        <div class="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-white/70 dark:bg-zinc-800/70 backdrop-blur-[1px]">
          <div class="animate-spin rounded-full h-5 w-5 border-2 border-zinc-300 dark:border-zinc-500 border-t-red-500"></div>
        </div>
      }

      <!-- Header Row -->
      <div
        class="flex items-center justify-between gap-2"
        [class.cursor-pointer]="canExpand()"
        (click)="onHeaderClick()"
        (keydown.enter)="onHeaderClick()"
        [attr.role]="canExpand() ? 'button' : null"
        [attr.tabindex]="canExpand() ? 0 : null"
        [attr.aria-expanded]="canExpand() ? isExpanded() : null"
      >
        <div class="flex items-center gap-2.5 min-w-0">
          <app-field-type-icon [type]="field().type" [size]="actionable() ? 'sm' : 'md'"/>

          <div class="flex items-center gap-2.5 min-w-0">
            <div class="flex flex-col min-w-0">
              <h3 data-testid="field-card-name" class="font-semibold text-zinc-900 dark:text-zinc-100 truncate"
                  [class.text-sm]="actionable()"
                  [class.text-base]="!actionable()"
                  [matTooltip]="field().name">{{ field().name }}</h3>
              @if (exampleTemplateValue()) {
                <span class="text-xs text-zinc-400 dark:text-zinc-500">{{ formatExampleValue() }}</span>
              }
            </div>

            <div class="flex items-center gap-1.5">
              @if (field().required) {
                <mat-icon
                  class="text-red-600 !text-sm !w-3.5 !h-3.5 !leading-3"
                  matTooltip="required field"
                >
                  *
                </mat-icon>
              }
              @if (field().isAlwaysVisible) {
                <mat-icon
                  class="text-green-400 !text-sm !w-3.5 !h-3.5 !leading-3"
                  matTooltip="default field">visibility
                </mat-icon>
              }
              @if (field().category) {
                <span class="inline-flex items-center rounded-full bg-indigo-50 dark:bg-indigo-900/30 px-2 py-0.5 text-[10px] font-medium text-indigo-600 dark:text-indigo-400">{{ field().category }}</span>
              }
              <!-- Structure usage dots -->
              @if (usedInStructures().length > 0) {
                <span
                  data-testid="field-card-structure-dots"
                  class="flex items-center gap-1 ml-0.5"
                  [matTooltip]="'Used in: ' + usedInStructureNames()"
                >
                  @for (s of usedInStructures(); track s.id) {
                    <span class="h-2 w-2 rounded-full ring-1 ring-white dark:ring-zinc-800" [style.backgroundColor]="s.color"></span>
                  }
                </span>
              }
            </div>
          </div>
        </div>

        <div class="flex items-center gap-0.5 shrink-0">
          @if (actionable()) {
            <!-- Actions: always visible, emphasized on hover -->
            <button
              data-testid="field-card-edit-btn"
              type="button"
              (click)="$event.stopPropagation(); edit.emit(field())"
              matTooltip="Edit"
              class="flex items-center justify-center h-7 w-7 rounded-md text-zinc-400 dark:text-zinc-500 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
            >
              <mat-icon class="!text-base !w-4 !h-4 !leading-4">edit</mat-icon>
            </button>
            <button
              data-testid="field-card-clone-btn"
              type="button"
              (click)="$event.stopPropagation(); clone.emit(field())"
              matTooltip="Clone"
              class="flex items-center justify-center h-7 w-7 rounded-md text-zinc-400 dark:text-zinc-500 hover:text-emerald-600 dark:hover:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
            >
              <mat-icon class="!text-base !w-4 !h-4 !leading-4">file_copy</mat-icon>
            </button>
            <button
              data-testid="field-card-copy-btn"
              type="button"
              (click)="$event.stopPropagation(); copyToClipboard()"
              matTooltip="Copy to clipboard"
              class="flex items-center justify-center h-7 w-7 rounded-md text-zinc-400 dark:text-zinc-500 hover:text-violet-600 dark:hover:text-violet-400 hover:bg-violet-50 dark:hover:bg-violet-900/30 transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500"
            >
              <mat-icon class="!text-base !w-4 !h-4 !leading-4">content_copy</mat-icon>
            </button>
            <button
              data-testid="field-card-delete-btn"
              type="button"
              (click)="$event.stopPropagation(); delete.emit(field())"
              matTooltip="Delete"
              class="flex items-center justify-center h-7 w-7 rounded-md text-zinc-400 dark:text-zinc-500 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
            >
              <mat-icon class="!text-base !w-4 !h-4 !leading-4">delete</mat-icon>
            </button>
          }

          @if (canExpand()) {
            <mat-icon
              data-testid="field-card-expand-icon"
              class="!text-base text-zinc-400 dark:text-zinc-500 transition-transform duration-200 flex-shrink-0 ml-1"
              [class.rotate-180]="isExpanded()">expand_more
            </mat-icon>
          }
        </div>
      </div>

      @if (showDetails()) {
      <!-- Description & Enum Values -->
      <div class="flex flex-col gap-3" [class.pl-12]="!actionable()" [class.pl-8]="actionable()">
        @if (field().description) {
          <p class="text-sm text-zinc-600 dark:text-zinc-400 leading-relaxed whitespace-pre-wrap">
            {{ field().description }}
          </p>
        }

        <!-- Example Template -->
        @if (exampleTemplateDescription()) {
          <div class="flex flex-col gap-1">
            <span class="block text-[10px] font-semibold text-zinc-400 dark:text-zinc-500 mb-0.5">Template instructions:</span>
            <span class="text-sm font-mono text-zinc-700 dark:text-zinc-300">{{ exampleTemplateDescription() }}</span>
          </div>
        }

        @if (field().enumValues && field().enumValues!.length > 0) {
          <div class="flex flex-wrap gap-1.5">
            @for (val of field().enumValues; track val) {
              <span
                class="inline-flex items-center rounded-full bg-zinc-100 dark:bg-zinc-700 px-2.5 py-0.5 text-xs font-medium text-zinc-600 dark:text-zinc-300 border border-zinc-200 dark:border-zinc-600">
                {{ val }}
              </span>
            }
          </div>
        }

        <!-- Used in structures -->
        @if (usedInStructures().length > 0) {
          <div class="flex flex-wrap items-center gap-1.5">
            <span class="text-[10px] font-semibold tracking-wide text-zinc-400 dark:text-zinc-500 uppercase">Used in</span>
            @for (s of usedInStructures(); track s.id) {
              <span class="inline-flex items-center gap-1.5 rounded-full bg-zinc-50 dark:bg-zinc-700 px-2 py-0.5 text-xs text-zinc-600 dark:text-zinc-300 border border-zinc-200 dark:border-zinc-600">
                <span class="h-1.5 w-1.5 rounded-full" [style.backgroundColor]="s.color"></span>
                {{ s.name }}
              </span>
            }
          </div>
        }

        @if (isObjectType() && composedFields().length > 0) {
          <div class="border-t border-zinc-100 dark:border-zinc-700 pt-3 mt-1">
            @if (field().type === 'object-list' && exampleListItems().length > 0) {
              <span class="block text-[10px] font-semibold tracking-wide text-zinc-400 dark:text-zinc-500 uppercase mb-2">Items <span class="normal-case">({{ exampleListItems().length }})</span> · <span class="normal-case">{{ composedFields().length }} field(s) each</span></span>
              <div class="space-y-4">
                @for (item of exampleListItems(); track $index) {
                  <div class="rounded-lg border border-dashed border-zinc-200 dark:border-zinc-600 p-3">
                    <span class="block text-[10px] font-semibold text-zinc-400 dark:text-zinc-500 mb-2">Item {{ $index + 1 }}</span>
                    <div class="space-y-2">
                      @for (cf of composedFields(); track cf.id) {
                        <app-field-card
                          class="block"
                          [field]="cf"
                          [allFields]="allFields()"
                          [exampleTemplateValue]="getNestedExampleValueAt(cf.id, $index)"
                          [exampleTemplateDescription]="getNestedExampleDescriptionAt(cf.id, $index)"
                          [actionable]="false"
                          [expandable]="true"
                        />
                      }
                    </div>
                  </div>
                }
              </div>
            } @else {
              <span class="block text-[10px] font-semibold tracking-wide text-zinc-400 dark:text-zinc-500 uppercase mb-2">Composed Fields <span class="normal-case">({{ composedFields().length }})</span></span>
              <div class="space-y-2">
                @for (cf of composedFields(); track cf.id) {
                  <app-field-card
                    class="block"
                    [field]="cf"
                    [allFields]="allFields()"
                    [exampleTemplateValue]="getNestedExampleValue(cf.id)"
                    [exampleTemplateDescription]="getNestedExampleDescription(cf.id)"
                    [actionable]="false"
                    [expandable]="true"
                  />
                }
              </div>
            }
          </div>
        }
      </div>
      }
    </div>
  `
})
export class FieldCardComponent {
  private clipboard = inject(Clipboard);
  private snackBar = inject(MatSnackBar);

  field = input.required<Field>();
  allFields = input<Field[]>([]);
  /** Structures referencing fields — used to render usage dots and "Used in" chips. */
  structures = input<StructureType[]>([]);
  /** When set, the card border/ring takes this color (used to link a hovered structure to its fields). */
  highlightColor = input<string | null>(null);
  exampleTemplateValue = input<any>();
  exampleTemplateDescription = input<string>();
  actionable = input<boolean>(true);
  expandable = input<boolean>(false);
  isDeleting = input<boolean>(false);

  isObjectType = computed(() => {
    const type = this.field().type;
    return type === 'object' || type === 'object-list';
  });

  exampleListItems = computed(() => {
    if (this.field().type !== 'object-list') return [];
    const val = this.exampleTemplateValue();
    if (!Array.isArray(val)) return [];
    return val;
  });

  composedFields = computed(() => {
    const fieldIds = this.field().fieldIds;
    if (!fieldIds || fieldIds.length === 0) return [];
    const all = this.allFields();
    if (!all.length) return [];
    const map = new Map(all.map(f => [f.id, f]));
    return fieldIds.map(id => map.get(id)).filter((f): f is Field => !!f);
  });

  usedInStructures = computed(() => {
    const id = this.field().id;
    return this.structures().filter(s => s.fieldIds.includes(id));
  });

  usedInStructureNames = computed(() =>
    this.usedInStructures().map(s => s.name).join(', ')
  );

  /** Both expandable mode (read-only contexts) and actionable mode (schema page) expand on click. */
  canExpand = computed(() => {
    if (this.expandable() && !this.actionable()) return true;
    if (!this.actionable()) return false;
    return !!(
      this.field().description ||
      this.field().enumValues?.length ||
      this.composedFields().length ||
      this.usedInStructures().length
    );
  });

  showDetails = computed(() => {
    if (this.expandable() && !this.actionable()) return this.isExpanded();
    if (this.actionable()) return this.canExpand() && this.isExpanded();
    // Read-only, non-expandable contexts (e.g., anchor sidebar) show only the header row.
    return false;
  });

  edit = output<Field>();
  clone = output<Field>();
  delete = output<Field>();

  isExpanded = signal(false);

  onHeaderClick(): void {
    if (!this.canExpand()) return;
    this.isExpanded.update(v => !v);
  }

  copyToClipboard(): void {
    const field = this.field();
    const fieldData = [{
      id: field.id,
      name: field.name,
      category: field.category,
      type: field.type,
      required: field.required,
      isAlwaysVisible: field.isAlwaysVisible,
      description: field.description || '',
      enumValues: field.enumValues || [],
      defaultValue: field.defaultValue,
      ...(field.fieldIds?.length ? { fieldIds: field.fieldIds } : {}),
      ...(typeof field.minTolerance === 'number' ? { minTolerance: field.minTolerance } : {})
    }];
    const json = JSON.stringify(fieldData, null, 2);
    this.clipboard.copy(json);
    this.snackBar.open('Field copied to clipboard', 'OK', { duration: 2000 });
  }

  formatDefaultValue(val: Field['defaultValue']): string {
    if (val === null || val === undefined) return '';
    if (Array.isArray(val)) return JSON.stringify(val);
    return String(val);
  }

  formatExampleValue(): string {
    const val = this.exampleTemplateValue();
    if (val == null) return '';
    if (typeof val === 'object') {
      if (Array.isArray(val)) return `${val.length} item(s)`;
      return `{${Object.keys(val).length} field(s)}`;
    }
    return String(val);
  }

  getNestedExampleValue(fieldId: string): any {
    const val = this.exampleTemplateValue();
    if (val == null || typeof val !== 'object') return undefined;
    const obj = Array.isArray(val) ? val[0] : val;
    if (!obj || typeof obj !== 'object') return undefined;
    const nested = (obj as Record<string, any>)[fieldId];
    if (nested == null) return undefined;
    return nested?.exampleValue ?? undefined;
  }

  getNestedExampleDescription(fieldId: string): string | undefined {
    const val = this.exampleTemplateValue();
    if (val == null || typeof val !== 'object') return undefined;
    const obj = Array.isArray(val) ? val[0] : val;
    if (!obj || typeof obj !== 'object') return undefined;
    const nested = (obj as Record<string, any>)[fieldId];
    return nested?.description ?? undefined;
  }

  getNestedExampleValueAt(fieldId: string, index: number): any {
    const val = this.exampleTemplateValue();
    if (!Array.isArray(val) || !val[index]) return undefined;
    const obj = val[index];
    if (typeof obj !== 'object') return undefined;
    const nested = (obj as Record<string, any>)[fieldId];
    if (nested == null) return undefined;
    return nested?.exampleValue ?? undefined;
  }

  getNestedExampleDescriptionAt(fieldId: string, index: number): string | undefined {
    const val = this.exampleTemplateValue();
    if (!Array.isArray(val) || !val[index]) return undefined;
    const obj = val[index];
    if (typeof obj !== 'object') return undefined;
    const nested = (obj as Record<string, any>)[fieldId];
    return nested?.description ?? undefined;
  }
}
